/**
 * Variant Quality Report — print layout (#783 Slice 1).
 *
 * Presentation-only. Renders solely from the `VariantQualityViewModel`
 * fed to it; never queries Mongo, calls domain services, or makes
 * additional API requests. That makes the report (a) deterministic
 * for the same view model, (b) trivially snapshot-able for future
 * audit/archive use, and (c) immune to drift from the live dashboard.
 *
 * The trend sparkline is hand-rolled SVG — no chart library, no
 * animations, no SSR/headless-render hazards.
 */
import { cn } from '@/lib/utils'
import { fmtDate, fmtDateTime } from '../format'
import type {
  VariantQualityViewModel,
  VqHealthVerdict,
  VqSuiteTest,
  VqTrendPoint,
} from '../types'

interface VariantQualityLayoutProps {
  viewModel: VariantQualityViewModel
}

export function VariantQualityLayout({ viewModel }: VariantQualityLayoutProps) {
  const {
    context,
    headline,
    health,
    trend,
    attention,
    coldStart,
    definition,
    suiteTests,
  } = viewModel
  // Discriminator for "suite filter was applied". Uses `suiteTests`
  // (not `context.suiteName`) so the orchestrator's decision is the
  // single source of truth — a suite-filtered report whose suite
  // name lookup failed (deleted suite, race condition) still
  // renders as a suite report, not as a variant-wide report. The
  // orchestrator only populates `suiteTests` when `suiteId` was
  // passed, regardless of whether the name resolved.
  const isSuiteFiltered = suiteTests !== null
  // When a suite filter is applied, the SUITE is the focal identity
  // of the report — promote its name to the H1 and demote the
  // variant to a subtitle line. Without a suite filter, the variant
  // is still the title (the report is a variant-wide health view).
  // Falls back to `(unknown suite)` for the deleted-suite case so
  // the H1 never goes blank.
  const titleText = isSuiteFiltered
    ? (context.suiteName ?? '(unknown suite)')
    : context.variantName
  const subtitleParts = isSuiteFiltered
    ? [context.appName, context.variantName].filter(Boolean)
    : [context.appName]
  return (
    <div className="bg-white text-gray-900 mx-auto max-w-[8.5in] p-8 font-sans text-sm leading-snug">
      {/* Header */}
      <header className="border-b border-gray-300 pb-4">
        {/* "OttoTester ·" prefix dropped in #841 — brand identity is
            already established by the per-page PDF header (logo +
            wordmark) AND the footer; a third instance on the body
            eyebrow was redundant. */}
        <div className="text-[10px] tracking-widest text-gray-500">
          Quality Report
        </div>
        <h1 className="mt-1 text-2xl font-bold">{titleText}</h1>
        <div className="mt-0.5 text-sm text-gray-600">
          {subtitleParts.join(' · ')}
        </div>
        <div className="mt-3 text-xs text-gray-500">
          {context.windowLabel} ·{' '}
          {isSuiteFiltered
            ? `Suite: ${context.suiteName ?? '(unknown)'}`
            : 'All suites'}{' '}
          · {context.runTypeLabel} · Generated{' '}
          {fmtDateTime(viewModel.generatedAt)}
        </div>
      </header>

      {coldStart && (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Cold start — fewer than 5 runs in window. Numbers may not be
          representative of steady-state quality.
        </div>
      )}

      {/* Headline metrics */}
      <section className="mt-5 grid grid-cols-4 gap-3">
        {headline.map((m) => (
          <div key={m.label} className="rounded border border-gray-200 p-3">
            <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
              {m.label}
            </div>
            <div className="mt-1 text-xl font-bold tabular-nums leading-tight">
              {m.value}
            </div>
            <div
              className={cn(
                'mt-1 text-[11px] tabular-nums',
                m.delta?.direction === 'up' && 'text-green-700',
                m.delta?.direction === 'down' && 'text-red-700',
                m.delta?.direction === 'flat' && 'text-gray-500',
                !m.delta && 'text-transparent select-none',
              )}
            >
              {m.delta?.text ?? '·'}
            </div>
          </div>
        ))}
      </section>

      {/* Health verdict */}
      {health && (
        <section className="mt-4">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Health
          </div>
          <div className="mt-1">
            <HealthBadge health={health} />
          </div>
        </section>
      )}

      {/* Pass-rate trend */}
      {trend.length > 0 && (
        <section className="mt-5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Pass-rate trend ({trend.length}{' '}
            {trend.length === 1 ? 'run' : 'runs'})
          </div>
          <Trendline points={trend} />
          <div className="mt-1 flex justify-between text-[10px] text-gray-500">
            <span>{fmtDate(trend[0]?.createdAt ?? '')}</span>
            <span>{fmtDate(trend[trend.length - 1]?.createdAt ?? '')}</span>
          </div>
        </section>
      )}

      {/* Triage — the three buckets the dashboard's triage tabs
          render (#832 expanded from just new+persistent to all
          three so the PDF matches the dashboard's content). Each
          bucket is its own subsection: clearer reading than a
          unified table with a Kind column. */}
      <TriageSection attention={attention} />

      {/* Tests in suite — populated only when a suite filter is
          applied. Lists every test from the suite's most recent run
          in the window so the reader has a full roster of what's
          in the suite, not just the failures the triage panel
          surfaces. Variant-wide reports skip this section
          entirely (suiteTests === null). */}
      {suiteTests && (
        <section className="mt-5">
          <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Tests in suite{suiteTests.length > 0 ? <> ({suiteTests.length})</> : null}
          </div>
          {suiteTests.length === 0 ? (
            <div className="rounded border border-dashed border-gray-300 p-4 text-center text-sm text-gray-600">
              No runs in this window — nothing to list yet.
            </div>
          ) : (
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-300 text-left text-gray-600">
                  <th className="py-1.5 pr-2 font-medium">Test</th>
                  <th className="w-24 py-1.5 pr-2 font-medium">Status</th>
                  <th className="w-20 py-1.5 font-medium tabular-nums">Duration</th>
                </tr>
              </thead>
              <tbody>
                {suiteTests.map((t, i) => (
                  <tr
                    key={`${t.testName}-${i}`}
                    className="border-b border-gray-100"
                  >
                    <td className="py-1.5 pr-2 font-mono">{t.testName}</td>
                    <td className="py-1.5 pr-2">
                      <SuiteTestStatus status={t.status} />
                    </td>
                    <td className="py-1.5 tabular-nums">
                      {fmtRowDuration(t.durationMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* Footer */}
      <footer className="mt-8 border-t border-gray-300 pt-3 text-[10px] text-gray-500">
        <div>
          Generated by OttoTester · {definition.id} v{definition.version} ·{' '}
          {fmtDateTime(viewModel.generatedAt)}
        </div>
        <div className="mt-1">
          Live view &amp; detail:{' '}
          <a className="underline" href={viewModel.liveViewUrl}>
            {viewModel.liveViewUrl}
          </a>
        </div>
        <div className="mt-1 italic">
          Detail (sparklines, full triage, the Compare view) is in OttoTester.
        </div>
      </footer>
    </div>
  )
}

// ── Internal components ─────────────────────────────────────────────

function HealthBadge({ health }: { health: VqHealthVerdict }) {
  const toneClass =
    health.tone === 'success'
      ? 'border-green-300 bg-green-50 text-green-800'
      : health.tone === 'destructive'
      ? 'border-red-300 bg-red-50 text-red-800'
      : 'border-amber-300 bg-amber-50 text-amber-800'
  return (
    <span
      className={cn(
        'inline-block rounded border px-2 py-0.5 text-xs font-semibold',
        toneClass,
      )}
    >
      {health.label}
    </span>
  )
}

/** Hand-rolled SVG sparkline. No chart library — keeps the headless
 * render deterministic and the bundle slim. */
function Trendline({ points }: { points: VqTrendPoint[] }) {
  const W = 700
  const H = 60
  const n = points.length
  if (n === 0) return null
  // Single-point trend renders as a small dot rather than a line so a
  // cold-start variant still draws something legible.
  if (n === 1) {
    const p = points[0]
    if (!p) return null
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mt-1 h-14 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label="Pass-rate trend"
      >
        <line x1={0} y1={H} x2={W} y2={H} stroke="#e5e7eb" />
        <circle cx={W / 2} cy={H - p.passRate * H} r={3} fill="#16a34a" />
      </svg>
    )
  }
  const d = points
    .map((p, i) => {
      const x = (i / (n - 1)) * W
      const y = H - p.passRate * H
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="mt-1 h-14 w-full"
      preserveAspectRatio="none"
      role="img"
      aria-label="Pass-rate trend"
    >
      {/* baseline */}
      <line x1={0} y1={H} x2={W} y2={H} stroke="#e5e7eb" />
      {/* 100% reference */}
      <line x1={0} y1={0} x2={W} y2={0} stroke="#f3f4f6" />
      <path d={d} fill="none" stroke="#16a34a" strokeWidth={1.5} />
    </svg>
  )
}

/** Per-row status indicator for the "Tests in suite" table. Visually
 * compact (badge instead of a status dot + label) because the table
 * row is already dense. */
function SuiteTestStatus({ status }: { status: VqSuiteTest['status'] }) {
  const isFailing =
    status === 'failed' || status === 'timedOut' || status === 'interrupted'
  const tone = isFailing
    ? 'border-red-300 bg-red-50 text-red-800'
    : status === 'skipped'
      ? 'border-gray-300 bg-gray-50 text-gray-700'
      : 'border-emerald-300 bg-emerald-50 text-emerald-800'
  return (
    <span
      className={cn(
        'inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium',
        tone,
      )}
    >
      {status}
    </span>
  )
}

/** Per-row duration formatter. Sub-second tests show "<1s" rather
 * than the "—" fallback from `fmtDuration` (which is meant for
 * "no data"); 0-duration is a legitimate fast-test result here. */
function fmtRowDuration(ms: number): string {
  if (!isFinite(ms) || ms < 0) return '—'
  if (ms < 1000) return '<1s'
  const totalSeconds = Math.round(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes === 0) return `${seconds}s`
  return `${minutes}m ${seconds}s`
}


// ── Triage section (three buckets, one subsection each) ─────────────

/** Bucket-ordered triage section. Three subsections (New / Persistent
 * / Flaky); each renders its own table with bucket-appropriate
 * columns (Persistent gets a Streak column the others don't). The
 * dashboard's triage tabs surface the same three buckets — the PDF
 * just shows them all at once rather than gating on tab selection
 * (#832). Empty state for the whole section when no bucket has rows. */
function TriageSection({
  attention,
}: {
  attention: ReturnType<typeof groupByBucket>['attention']
}) {
  const groups = groupByBucket(attention)
  const totalRows = groups.new.length + groups.persistent.length + groups.flaky.length
  // Render the same top-level "Triage" header in both empty and
  // populated states so the section's identity is stable for the
  // reader regardless of data (Copilot review on #834).
  return (
    <section className="mt-5 space-y-4">
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
        Triage
      </div>
      {totalRows === 0 ? (
        <div className="rounded border border-dashed border-gray-300 p-4 text-center text-sm text-gray-600">
          No actionable failures in this window.
        </div>
      ) : (
        <>
          {groups.new.length > 0 && (
            <TriageBucket title="New failures" rows={groups.new} />
          )}
          {groups.persistent.length > 0 && (
            <TriageBucket title="Persistent failures" rows={groups.persistent} />
          )}
          {groups.flaky.length > 0 && (
            <TriageBucket title="Flaky" rows={groups.flaky} />
          )}
        </>
      )}
    </section>
  )
}

/** Pure split-by-kind. Used as the type accessor for the
 * `TriageSection` prop above; the function itself stays local. */
function groupByBucket(attention: { testName: string; kind: 'new' | 'persistent' | 'flaky'; streak?: number; errorType?: string }[]) {
  const newGroup: typeof attention = []
  const persistent: typeof attention = []
  const flaky: typeof attention = []
  for (const r of attention) {
    if (r.kind === 'new') newGroup.push(r)
    else if (r.kind === 'persistent') persistent.push(r)
    else flaky.push(r)
  }
  return { attention, new: newGroup, persistent, flaky }
}

/** One bucket's table — name + (Streak for persistent only) + Error
 * type. Header row repeats per bucket so readers don't have to
 * scroll back to a single thead when scanning multiple buckets. */
function TriageBucket({
  title,
  rows,
}: {
  title: string
  rows: { testName: string; kind: 'new' | 'persistent' | 'flaky'; streak?: number; errorType?: string }[]
}) {
  const showStreak = rows.some((r) => r.kind === 'persistent')
  return (
    <div>
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
        {title} ({rows.length})
      </div>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-gray-300 text-left text-gray-600">
            <th className="py-1.5 pr-2 font-medium">Test</th>
            {showStreak && (
              <th className="w-16 py-1.5 pr-2 font-medium">Streak</th>
            )}
            <th className="w-36 py-1.5 font-medium">Error type</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.testName}-${i}`} className="border-b border-gray-100">
              <td className="py-1.5 pr-2 font-mono">{r.testName}</td>
              {showStreak && (
                <td className="py-1.5 pr-2 tabular-nums">{r.streak ?? '—'}</td>
              )}
              <td className="py-1.5">{r.errorType ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
